import { useState, useEffect, useMemo } from 'react';
import { useMatrixClient } from '../context/MatrixContext';
import logger from '../utils/logger';

/**
 * Extract clean username from Matrix ID
 * @param {string} userId - Matrix user ID like @user:server.com
 * @returns {string} - Clean username like "user"
 */
const extractUsername = (userId) => {
  if (!userId) return '';
  if (userId.startsWith('@') && userId.includes(':')) {
    return userId.split(':')[0].substring(1);
  }
  return userId;
};

/**
 * Hook to fetch and track a user's display name from Matrix
 *
 * Attempts to get the display name from:
 * 1. Client cache (fast, synchronous)
 * 2. Server profile API (slower, requires network request)
 *
 * Falls back to extracted username if no display name is set.
 * Never returns the raw Matrix ID as display name.
 *
 * @param {string} userId - Matrix user ID
 * @param {string} fallback - Fallback display name (typically username or userId)
 * @returns {string} The user's display name or fallback
 */
export const useUserDisplayName = (userId, fallback = '') => {
  const client = useMatrixClient();

  // Compute clean fallback: prefer provided fallback if it's not a Matrix ID,
  // otherwise extract username from userId
  const cleanFallback = useMemo(() => {
    if (fallback && !fallback.startsWith('@')) {
      return fallback;
    }
    return extractUsername(userId) || fallback || 'Guest';
  }, [fallback, userId]);

  const [displayName, setDisplayName] = useState(cleanFallback);

  useEffect(() => {
    const fetchDisplayName = async () => {
      if (!client || !userId) {
        setDisplayName(cleanFallback);
        return;
      }

      try {
        // Try to get user from client cache first (fast path)
        const user = client.getUser(userId);
        // Only use cached displayName if it's not just the Matrix ID
        if (user?.displayName && user.displayName !== userId) {
          setDisplayName(user.displayName);
          return;
        }

        // If not in cache, fetch from server
        const profile = await client.getProfileInfo(userId);
        // Only use server displayname if it's not just the Matrix ID
        if (profile?.displayname && profile.displayname !== userId) {
          setDisplayName(profile.displayname);
        } else {
          // No valid display name set, use clean fallback
          setDisplayName(cleanFallback);
        }
      } catch (error) {
        logger.debug('Could not fetch display name for', userId, ':', error);
        // Keep using clean fallback
        setDisplayName(cleanFallback);
      }
    };

    fetchDisplayName();
  }, [client, userId, cleanFallback]);

  return displayName;
};

export default useUserDisplayName;
